// 主導覽：桌面與平板是左側欄，手機是底部分頁列。入口是一般連結（可以在新分頁開啟），
// 一般點擊時直接更新狀態，不重新載入頁面。
import type { MouseEvent, ReactNode } from 'react';
import { monthOfDay } from '../../filters/calendar.ts';
import { type FilterState, serializeState } from '../../filters/state.ts';
import { localEpochDay } from '../../filters/timeline.ts';
import type { Update } from '../../filters/useFilterState.ts';
import { t } from '../../i18n/zh-TW.ts';
import { CalendarDays, History, House, ICON, type LucideIcon, Search } from '../../ui/icons/index.ts';
import { activeSection, enterSection, SECTIONS, type Section } from './nav.ts';

const ITEMS: Record<Section, { label: string; Icon: LucideIcon }> = {
  upcoming: { label: t.nav.upcoming, Icon: House },
  calendar: { label: t.nav.calendar, Icon: CalendarDays },
  released: { label: t.nav.released, Icon: History },
};

interface NavProps {
  state: FilterState;
  update: (u: Update) => void;
  /** 未發售遊戲數（側欄顯示在「即將發售」旁） */
  upcomingCount?: number;
}

function NavLinks({ state, update, upcomingCount, className }: NavProps & { className: string }) {
  const active = activeSection(state);
  const thisMonth = monthOfDay(localEpochDay());
  const go = (e: MouseEvent, section: Section) => {
    // 按住修飾鍵（新分頁、新視窗）時交給瀏覽器
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    update((s) => enterSection(s, section, thisMonth));
    window.scrollTo({ top: 0 });
  };
  return (
    <>
      {SECTIONS.map((section) => {
        const { label, Icon } = ITEMS[section];
        const href = serializeState(enterSection(state, section, thisMonth)) || window.location.pathname;
        return (
          <a
            key={section}
            className={className}
            href={href}
            aria-current={active === section ? 'page' : undefined}
            // 平板只顯示圖示時文字被隱藏，名稱由 aria-label 提供
            aria-label={
              section === 'upcoming' && upcomingCount !== undefined
                ? `${label}（${t.nav.upcomingCount(upcomingCount)}）`
                : label
            }
            onClick={(e) => go(e, section)}
            title={label}
          >
            <Icon {...ICON} />
            <span className="nav-label">{label}</span>
            {section === 'upcoming' && upcomingCount !== undefined && (
              <span className="nav-count num" aria-hidden="true">
                {upcomingCount}
              </span>
            )}
          </a>
        );
      })}
    </>
  );
}

export function Logo({ as: Tag = 'div' }: { as?: 'h1' | 'div' }) {
  return (
    <Tag className="logo">
      <span className="sr-only">{t.app.name}</span>
      <span className="logo-full" aria-hidden="true">
        DAY<b>·</b>ONE
      </span>
      <span className="logo-short" aria-hidden="true">
        D<b>1</b>
      </span>
    </Tag>
  );
}

/** 桌面：完整側欄；平板：只有圖示（CSS 依寬度切換） */
export function Rail(props: NavProps & { footer: ReactNode }) {
  return (
    <aside className="rail">
      <Logo as="h1" />
      <nav className="rail-nav" aria-label={t.nav.label}>
        <NavLinks {...props} className="rail-link" />
      </nav>
      <div className="rail-foot">{props.footer}</div>
    </aside>
  );
}

/** 手機：底部分頁列，最後一格是搜尋 */
export function TabBar(props: NavProps & { onSearch: () => void }) {
  return (
    <nav className="tabbar" aria-label={t.nav.label}>
      <NavLinks {...props} upcomingCount={undefined} className="tab" />
      <button type="button" className="tab" onClick={props.onSearch}>
        <Search {...ICON} />
        <span className="nav-label">{t.nav.search}</span>
      </button>
    </nav>
  );
}
